import { useCallback, useEffect, useMemo, useState } from 'react';
import { getMultiSignActionById } from '@/services/multiSign';
import {
  MultiSignActionAuthorisationStateEnum,
  TMultiSignAction,
  TMultiSignActionAuthorisationState
} from '@/services/types/MultiSign';
import {
  ScheduledBankPaymentGroupStateEnum,
  TScheduledBankPaymentGroupState
} from '@/features/payments/types/BatchPayment';
import { useTeamMemberNameLookup } from './useTeamMemberNameLookup';

export type TMultiSignApprover = {
  /** The authorisation's own id - stable even if two people share a display name. */
  id: string;
  name: string;
  state: TMultiSignActionAuthorisationState;
  /** A rejection's reason (mirrors RqRejectMultiSignAction.notes) - absent for approved/pending. */
  notes?: string | null;
};

type TAuthorisation = {
  id: string;
  customerId: string;
  state: TMultiSignActionAuthorisationState;
  notes?: string | null;
};

// Session-scoped cache of every authorisation on the action (not just approved ones - full
// visibility means showing who rejected and who's still pending too), keyed by multi-sign
// action id. Reopening the modal for the same row skips the action fetch entirely. Caches the
// raw customerIds rather than resolved names, since names are looked up live against
// HomeContext's team member list - no separate name cache is needed (see
// useTeamMemberNameLookup).
const authorisationsCache = new Map<string, TAuthorisation[]>();

// Every currently-mounted useMultiSignApprovers() instance for a given action id, so a write to
// the cache above from ONE of them (e.g. BatchOverviewModal's own approve() patching its own
// entry) can push the fresh list to every OTHER instance watching the same id too - BatchModal's
// summary card (which never unmounts while Batch Overview is open on top of it - it just stops
// rendering its own content) and PaymentStatusPill (a separate, always-mounted instance per list
// row) would otherwise keep showing whatever they first fetched, forever, since a plain module
// Map mutation doesn't itself trigger anyone else's re-render. See notifyAuthorisationsChanged.
const listenersByActionId = new Map<string, Set<() => void>>();

const notifyAuthorisationsChanged = (multiSignActionId: string) => {
  listenersByActionId.get(multiSignActionId)?.forEach((listener) => listener());
};

// Monotonically increasing write counter per action id - guards against a slow in-flight GET
// (the mount effect's initial fetch, or another refetch) resolving AFTER a newer write (a patch,
// or a different refetch) has already landed for the same action, and unconditionally clobbering
// it with older data. Without this, e.g. an initial fetch issued when a modal opens could still
// be in flight when its own approve() call resolves and patches the cache - and since the patch
// notifies every mounted listener, that fresher state is already visible everywhere - only for
// the slower initial fetch's stale response to arrive moments later, overwrite the cache back to
// the pre-approval list, and re-notify every listener with it. Every write (initial fetch,
// refetch, patch) bumps this via writeAuthorisationsCache before writing/notifying; a fetch whose
// captured version no longer matches when it resolves knows a newer write beat it and skips both.
const cacheVersionByActionId = new Map<string, number>();

const writeAuthorisationsCache = (multiSignActionId: string, authorisations: TAuthorisation[]) => {
  cacheVersionByActionId.set(
    multiSignActionId,
    (cacheVersionByActionId.get(multiSignActionId) ?? 0) + 1
  );
  authorisationsCache.set(multiSignActionId, authorisations);
  notifyAuthorisationsChanged(multiSignActionId);
};

const toAuthorisations = (action: TMultiSignAction): TAuthorisation[] =>
  action.authorisations.map((authorisation) => ({
    id: authorisation.id,
    customerId: authorisation.customerId,
    state: authorisation.state,
    notes: authorisation.notes
  }));

/**
 * Optimistically patches the cached authorisation entry for one customer on one action, so a
 * caller that already knows the outcome of its own approve/reject call (see
 * useScheduledPaymentAction) doesn't leave the cache holding the pre-decision list. Without this,
 * reopening the same batch/payment later in the same session - e.g. Batch Overview's "who
 * rejected this" name, right after rejecting it - would keep reading the stale cached list
 * (nobody shows as having approved/rejected yet) until a hard refresh cleared this module-level
 * cache and forced a real re-fetch. A no-op if nothing has been fetched (and thus cached) for
 * this action yet - the next real fetch will already come back correct.
 *
 * Also notifies every other currently-mounted useMultiSignApprovers() instance for this same
 * action id, so a still-open BatchModal summary (or the list's PaymentStatusPill) reflects a
 * non-final approve immediately, without needing to unmount/remount to pick up the patched cache.
 */
export const patchCachedAuthorisation = (
  multiSignActionId: string,
  customerId: string,
  state: TMultiSignActionAuthorisationState,
  notes?: string | null
) => {
  const cached = authorisationsCache.get(multiSignActionId);
  if (!cached) return;

  writeAuthorisationsCache(
    multiSignActionId,
    cached.map((authorisation) =>
      authorisation.customerId === customerId ? { ...authorisation, state, notes } : authorisation
    )
  );
};

/**
 * The group's own approverCustomerId isn't reliable enough to show the full picture once a rule
 * requires more than one sign-off - whether that's who's approved/rejected so far while still
 * PENDING_APPROVAL, or the full picture once the action has been decided. This fetches the
 * action's full authorisation list and resolves the name of every participant, whichever way
 * they voted, plus the current user's own authorisation (if any) so a caller can tell "have I
 * already acted on this" without re-deriving it from the raw list.
 *
 * `paymentState` (optional) additionally derives two related but distinct flags, both scoped to
 * "still PENDING_APPROVAL" (more approvers are still required either way):
 * - `hasPersistedApproval` - true only when THIS specific viewer's own authorisation shows
 *   APPROVED. Drives anything that credits/addresses this viewer personally (e.g. the "You have
 *   approved this payment" banner) - it must not fire on someone else's approval.
 * - `hasAnyApproval` - true once ANY authorisation on the action shows APPROVED, regardless of
 *   whose. Drives the wider "Additional approval required" labelling - a batch already has one
 *   sign-off and is waiting on more, which is true for every remaining approver to see, not just
 *   whoever actually signed.
 * Every caller that shows a modal needs these same checks, so they live here once rather than
 * being re-derived in each of ScheduledPaymentModal, BatchModal, BatchOverviewModal, and
 * PaymentStatusPill.
 */
export const useMultiSignApprovers = (
  multiSignActionId?: string,
  enabled = true,
  currentCustomerId?: string,
  paymentState?: TScheduledBankPaymentGroupState
) => {
  const lookupName = useTeamMemberNameLookup();
  const [authorisations, setAuthorisations] = useState<TAuthorisation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!multiSignActionId || !enabled) {
      setAuthorisations([]);
      return;
    }

    const cached = authorisationsCache.get(multiSignActionId);
    if (cached) {
      setAuthorisations(cached);
      return;
    }

    let isCancelled = false;
    // Snapshot the write version before this request goes out - see cacheVersionByActionId. If
    // it's moved on by the time this resolves, a newer write (a patch, or another refetch) beat
    // this response to the cache, so this one is stale and must not overwrite it.
    const versionAtFetchStart = cacheVersionByActionId.get(multiSignActionId) ?? 0;
    setIsLoading(true);

    getMultiSignActionById(multiSignActionId)
      .then((response) => {
        const all = toAuthorisations(response.data);
        if ((cacheVersionByActionId.get(multiSignActionId) ?? 0) === versionAtFetchStart) {
          writeAuthorisationsCache(multiSignActionId, all);
          if (!isCancelled) setAuthorisations(all);
        } else if (!isCancelled) {
          // Adopt whatever the newer write left behind instead of this now-stale response.
          setAuthorisations(authorisationsCache.get(multiSignActionId) ?? all);
        }
      })
      .catch(() => {
        if (!isCancelled) setAuthorisations([]);
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [multiSignActionId, enabled]);

  // Keeps this instance's own state in sync with the shared cache being patched/refetched by a
  // DIFFERENT useMultiSignApprovers() instance for the same action id (see
  // patchCachedAuthorisation/refetch and listenersByActionId above) - without this, a still-open
  // BatchModal summary (which never unmounts while Batch Overview is open on top of it) or the
  // list's PaymentStatusPill would keep showing whatever they first fetched, forever.
  useEffect(() => {
    if (!multiSignActionId || !enabled) return;

    const listener = () => {
      const cached = authorisationsCache.get(multiSignActionId);
      if (cached) setAuthorisations(cached);
    };

    let listeners = listenersByActionId.get(multiSignActionId);
    if (!listeners) {
      listeners = new Set();
      listenersByActionId.set(multiSignActionId, listeners);
    }
    listeners.add(listener);

    return () => {
      listeners!.delete(listener);
      if (listeners!.size === 0) listenersByActionId.delete(multiSignActionId);
    };
  }, [multiSignActionId, enabled]);

  /**
   * Forces a live re-fetch, bypassing the cache-hit shortcut above, and updates both the shared
   * cache and this hook's own state with the result - unlike the mount effect, a caller invokes
   * this explicitly when it already knows the cached list is stale (see
   * reconcileAfterAuthorisationConflict in useScheduledPaymentAction: a 4100 "authorisation not
   * pending" response means someone - possibly this same customer from another device - already
   * acted on this action since it was last fetched). Returns the fresh action so the caller can
   * also reconcile the group/payment's own `state` field, which isn't derived from this hook.
   *
   * The returned action is always this call's own just-fetched server response, regardless of
   * whether the shared cache below ends up adopting it - a caller reconciling the payment's own
   * `state` needs what THIS request actually returned, not whatever another write happened to
   * win the race for the shared authorisations list. Same staleness guard as the mount effect's
   * fetch otherwise - see cacheVersionByActionId.
   */
  const refetch = useCallback(async (): Promise<TMultiSignAction | null> => {
    if (!multiSignActionId) return null;

    const versionAtRefetchStart = cacheVersionByActionId.get(multiSignActionId) ?? 0;
    try {
      const response = await getMultiSignActionById(multiSignActionId);
      const all = toAuthorisations(response.data);
      if ((cacheVersionByActionId.get(multiSignActionId) ?? 0) === versionAtRefetchStart) {
        writeAuthorisationsCache(multiSignActionId, all);
        setAuthorisations(all);
      } else {
        setAuthorisations(authorisationsCache.get(multiSignActionId) ?? all);
      }
      return response.data;
    } catch {
      return null;
    }
  }, [multiSignActionId]);

  const approvers: TMultiSignApprover[] = useMemo(
    () =>
      authorisations.map((authorisation) => ({
        id: authorisation.id,
        name: lookupName(authorisation.customerId),
        state: authorisation.state,
        notes: authorisation.notes
      })),
    [authorisations, lookupName]
  );

  const currentUserAuthorisation = useMemo(
    () => authorisations.find((authorisation) => authorisation.customerId === currentCustomerId),
    [authorisations, currentCustomerId]
  );

  const approvedAuthorisations = useMemo(
    () =>
      authorisations.filter(
        (authorisation) => authorisation.state === MultiSignActionAuthorisationStateEnum.APPROVED
      ),
    [authorisations]
  );

  // Scoped to this viewer's own authorisation - see the module doc comment above for how this
  // differs from hasAnyApproval below.
  const hasPersistedApproval =
    paymentState === ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL &&
    currentUserAuthorisation?.state === MultiSignActionAuthorisationStateEnum.APPROVED;

  // Any signatory's approval counts here, not just this viewer's own - true even when
  // hasPersistedApproval above is false (someone else approved, this viewer hasn't).
  const hasAnyApproval =
    paymentState === ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL &&
    approvedAuthorisations.length > 0;

  // True once the action's full authorisation list has loaded and the current user isn't one of
  // its signatories - they have no authority to approve/reject this action at all, as opposed to
  // hasPersistedApproval (they can act, and already have). Gated on !isLoading so a caller never
  // reads this as "no authority" while the fetch is still in flight - see the isLoadingApprovers
  // gating in every caller for why that distinction matters.
  const hasNoApprovalAuthority =
    !isLoading &&
    paymentState === ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL &&
    !currentUserAuthorisation;

  return {
    approvers,
    isLoading,
    currentUserAuthorisation,
    hasPersistedApproval,
    hasAnyApproval,
    hasNoApprovalAuthority,
    refetch
  };
};
